import React from "react";
import { motion, HTMLMotionProps } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  SwatchIcon,
  SpeakerWaveIcon,
  CubeTransparentIcon,
  AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";

const MotionImage = motion.img as React.FC<HTMLMotionProps<"img">>;

const Features: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = [
    {
      title: "サイコロの色変更",
      description:
        "お好みの色でゲームを楽しめます。複数のカラーテーマから選択可能です。",
      icon: SwatchIcon,
    },
    {
      title: "効果音カスタマイズ",
      description:
        "様々な効果音から選択可能。サイコロを振る音や結果発表時の音を設定できます。",
      icon: SpeakerWaveIcon,
    },
    {
      title: "お椀のデザイン",
      description:
        "複数のお椀デザインから選択できます。伝統的なものからモダンなものまで。",
      icon: CubeTransparentIcon,
    },
    {
      title: "投げるスピード調整",
      description:
        "サイコロを振るスピードを調整可能。緊張感のある演出を楽しめます。",
      icon: AdjustmentsHorizontalIcon,
    },
  ];

  return (
    <section id="features" className="py-20">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title text-center">カスタマイズ機能</h2>

          {/* カスタマイズ画像 */}
          <div className="flex justify-center mb-12">
            <MotionImage
              src="/images/customize_chinchiro.png"
              alt="カスタマイズダイスのプレビュー"
              className="rounded-lg shadow-2xl max-w-full h-auto"
              width={800}
              height={600}
              loading="lazy"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="card"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <feature.icon className="w-10 h-10 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-300 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
